import {
  getApigeeUrl,
  getEnv,
  getRegion,
  ENVS,
  REGIONS,
} from '@adsk/acs-sc-web-platform-api';
import PermissionSelect from '@adsk/alloy-react-permission-select';
import { SERVICE_ACCESSES } from '@common/constants';
import { SHEETS_PERMISSION_ACTIONS_MAP } from '../../constants';

const US_CONFIG = {
  [ENVS.QA]: `${getApigeeUrl()}/dm-qa`,
  [ENVS.STAGING]: `${getApigeeUrl()}/dm`,
  [ENVS.PRODUCTION]: `${getApigeeUrl()}/dm`,
};

const ENV_CONFIGS = {
  [REGIONS.US]: US_CONFIG,
  [REGIONS.EMEA]: US_CONFIG,
};

export const buildGetSheetsPermissionsRequest = (projectId, userId) => {
  const env = getEnv();
  const region = getRegion();

  return `${
    ENV_CONFIGS[region][env]
  }/v3/projects/${projectId}/sheet-permissions${
    userId ? `/users/${userId}` : ''
  }`;
};

const sheetsActionsToPermission = (permissions) => {
  if (permissions.includes(SHEETS_PERMISSION_ACTIONS_MAP.CONTROL.ACTION))
    return SHEETS_PERMISSION_ACTIONS_MAP.CONTROL;
  if (permissions.includes(SHEETS_PERMISSION_ACTIONS_MAP.EDIT.ACTION))
    return SHEETS_PERMISSION_ACTIONS_MAP.EDIT;
  if (permissions.includes('PUBLISH'))
    return SHEETS_PERMISSION_ACTIONS_MAP.CREATE;
  if (permissions.includes('PUBLISH_MARKUP'))
    return SHEETS_PERMISSION_ACTIONS_MAP.VIEW_DOWNLOAD_PUBLISH;
  if (
    permissions.includes(SHEETS_PERMISSION_ACTIONS_MAP.VIEW.ACTION) &&
    permissions.includes('DOWNLOAD')
  )
    return SHEETS_PERMISSION_ACTIONS_MAP.VIEW_DOWNLOAD;
  if (permissions.includes(SHEETS_PERMISSION_ACTIONS_MAP.VIEW.ACTION))
    return SHEETS_PERMISSION_ACTIONS_MAP.VIEW;
  return undefined;
};

const getSheetsSelectOptions = (T, selectedPermission) => {
  const permissionCollection = {};

  Object.values(SHEETS_PERMISSION_ACTIONS_MAP).forEach((permission) => {
    if (permission.hidden && permission.ACTION !== selectedPermission) return;
    if (permissionCollection[permission.GROUP]) {
      permissionCollection[permission.GROUP].list.push(permission);
    } else {
      permissionCollection[permission.GROUP] = {
        list: [permission],
        GROUP_ORDER: permission.ORDER,
        GROUP: permission.GROUP,
        LABEL: permission.LABEL,
        PILL_VALUE: permission.PILL_VALUE,
      };
    }
  });

  return Object.values(permissionCollection)
    .sort((a, b) => (a.GROUP_ORDER > b.GROUP_ORDER ? -1 : 1))
    .map((permissionGroup) => ({
      label: T.translate(
        `PERMISSIONS.LABELS.${permissionGroup.LABEL || permissionGroup.GROUP}`
      ),
      pillValue: permissionGroup.PILL_VALUE,
      options: permissionGroup.list
        .sort((a, b) => (a.ORDER > b.ORDER ? -1 : 1))
        .map((item) => ({
          value: item.ACTION,
          label: T.translate(`PERMISSIONS.SHEETS.${item.ACTION}_DESCRIPTION`),
          pillValue: item.PILL_VALUE,
          disabled: item.hidden,
        })),
    }));
};

const getSheetsInheritedPermissionData = (
  memberPermissionLevel,
  companyPermissionLevel,
  rolePermissionLevel,
  everyonePermissionsLevel,
  selectedAccessLevel,
  selectedCompanyName,
  selectedRoleName,
  T
) => {
  if (
    !memberPermissionLevel ||
    (memberPermissionLevel &&
      memberPermissionLevel.ACTION ===
        SHEETS_PERMISSION_ACTIONS_MAP.CONTROL.ACTION)
  )
    return null;

  if (selectedAccessLevel === SERVICE_ACCESSES.ADMIN) {
    return {
      levelName: T.translate('PERMISSIONS.LABELS.MANAGE'),
      subjectType: T.translate('PERMISSIONS.TYPES.GROUP_TEXT'),
      subjectName: 'Admin',
      pillValue: SHEETS_PERMISSION_ACTIONS_MAP.CONTROL.PILL_VALUE,
    };
  }

  let level = memberPermissionLevel.ORDER;
  let subjectName;
  let subjectType;
  let subjectGroup;
  let pillValue;

  if (companyPermissionLevel && companyPermissionLevel.ORDER < level) {
    level = companyPermissionLevel.ORDER;
    subjectType = T.translate('PERMISSIONS.TYPES.COMPANY_TEXT');
    subjectName = selectedCompanyName;
    subjectGroup = companyPermissionLevel.GROUP;
    pillValue = companyPermissionLevel.PILL_VALUE;
  }

  if (rolePermissionLevel && rolePermissionLevel.ORDER < level) {
    level = rolePermissionLevel.ORDER;
    subjectType = T.translate('PERMISSIONS.TYPES.ROLE_TEXT');
    subjectName = selectedRoleName;
    subjectGroup = rolePermissionLevel.GROUP;
    pillValue = rolePermissionLevel.PILL_VALUE;
  }

  if (everyonePermissionsLevel && everyonePermissionsLevel.ORDER < level) {
    level = everyonePermissionsLevel.ORDER;
    subjectType = T.translate('PERMISSIONS.TYPES.GROUP_TEXT');
    subjectName = T.translate('PERMISSIONS.SHEETS.EVERYONE_GROUP');
    subjectGroup = everyonePermissionsLevel.GROUP;
    pillValue = everyonePermissionsLevel.PILL_VALUE;
  }

  return level !== memberPermissionLevel.ORDER
    ? {
        levelName: T.translate(`PERMISSIONS.LABELS.${subjectGroup}`),
        subjectType,
        subjectName,
        pillValue,
      }
    : null;
};

const getRolePermissions = (rolesPermissions, selectedRoles) => {
  let higherPermissions = null;
  let roleName = null;

  rolesPermissions.forEach((rolePermissions) => {
    const rolePermissionLevel = sheetsActionsToPermission(
      rolePermissions.actions
    );
    if (
      !higherPermissions ||
      (higherPermissions && rolePermissionLevel.ORDER < higherPermissions.ORDER)
    ) {
      higherPermissions = rolePermissionLevel;
      roleName = selectedRoles.find(
        (role) => role.id === rolePermissions.subjectId
      ).name;
    }
  });

  return { rolePermissionLevel: higherPermissions, roleName };
};

export const getSheetsPermissionsInfo = (
  memberPermissions,
  companyPermissions,
  rolesPermissions,
  everyonePermissions,
  selectedAccessLevel,
  selectedCompany,
  selectedRoles,
  T
) => {
  const memberPermissionLevel = memberPermissions
    ? sheetsActionsToPermission(memberPermissions.actions)
    : null;
  const companyPermissionLevel = companyPermissions
    ? sheetsActionsToPermission(companyPermissions.actions)
    : null;
  const { rolePermissionLevel, roleName } = getRolePermissions(
    rolesPermissions,
    selectedRoles
  );
  const everyonePermissionsLevel = everyonePermissions
    ? sheetsActionsToPermission(everyonePermissions.actions)
    : null;
  const inheritedPermissionData = getSheetsInheritedPermissionData(
    memberPermissionLevel,
    companyPermissionLevel,
    rolePermissionLevel,
    everyonePermissionsLevel,
    selectedAccessLevel,
    selectedCompany?.name,
    roleName,
    T
  );
  const inheritedPermissions = !!inheritedPermissionData;
  let inheritanceInfo;
  let permissionsIndicatorProps;
  if (inheritedPermissions) {
    const { pillValue, levelName, subjectType, subjectName } =
      inheritedPermissionData;
    inheritanceInfo = T.translate('INHERITED_PERMISSIONS_INFO', {
      subjectType,
      subjectName,
    });
    permissionsIndicatorProps = {
      label: levelName,
      pillValue,
    };
  }

  return {
    selectValue: memberPermissionLevel && {
      value: memberPermissionLevel.ACTION,
    },
    permissionsIndicatorProps,
    inheritedPermissions,
    inheritanceInfo,
    selectOptions: getSheetsSelectOptions(T, memberPermissionLevel?.ACTION),
    selectType: PermissionSelect.displayName,
    selectValueLabel: memberPermissionLevel
      ? T.translate(
          `PERMISSIONS.LABELS.${
            memberPermissionLevel.LABEL
              ? memberPermissionLevel.LABEL
              : memberPermissionLevel.GROUP
          }`
        )
      : null,
    pillValue: memberPermissionLevel?.PILL_VALUE,
  };
};
